import React, { useEffect, useMemo, useState } from 'react'
import { TextField, Button, Box, Stack } from '@mui/material'
import { theme } from '../../theme.js'
import { MuiTelInput } from 'mui-tel-input'
import PropTypes from 'prop-types'
import { useForm, Controller } from 'react-hook-form'
import { useUser } from '../../hooks/useUser.js'
import { parseEmail } from '../../utils/validation'

const ProfileForm = ({ userData }) => {
  const { handleUpdateUserProfile } = useUser()
  const companyProfile = userData.company_profile || {}

  const initialValues = useMemo(() => ({
    username: userData.username || '',
    email: userData.email || '',
    company: userData.company || companyProfile.legal_name || '',
    registration_no: companyProfile.registration_no || '',
    fiscal_address: companyProfile.fiscal_address || '',
    county: companyProfile.county || '',
    city: companyProfile.city || '',
    postal_code: companyProfile.postal_code || '',
    phone: userData.phone || ''
  }), [
    companyProfile.city,
    companyProfile.county,
    companyProfile.fiscal_address,
    companyProfile.legal_name,
    companyProfile.postal_code,
    companyProfile.registration_no,
    userData.company,
    userData.email,
    userData.phone,
    userData.username
  ])

  const form = useForm({
    defaultValues: initialValues
  })
  const { register, handleSubmit, watch, reset, formState: { errors } } = form
  const [updateProfileMessage, setUpdateProfileMessage] = useState({ message: '', messageType: '' })
  const [loading, setLoading] = useState(false)

  const watchedFields = watch()

  useEffect(() => {
    reset(initialValues)
  }, [initialValues, reset])

  const isFormUnchanged = Object.keys(initialValues).every(
    key => (watchedFields[key] || '') === (initialValues[key] || '')
  )

  const onSubmit = async (data) => {
    setUpdateProfileMessage({ message: '', messageType: '' })
    setLoading(true) // Start loading
    try {
      const message = await handleUpdateUserProfile({
        username: data.username,
        email: data.email,
        phone: data.phone,
        company: data.company,
        company_profile: {
          legal_name: data.company,
          registration_no: data.registration_no,
          fiscal_address: data.fiscal_address,
          county: data.county,
          city: data.city,
          postal_code: data.postal_code
        }
      })
      setUpdateProfileMessage({ message, messageType: 'success' })
    } catch (error) {
      setUpdateProfileMessage({ message: 'Update profile failed: ' + error.message, messageType: 'error' })
    } finally {
      setLoading(false)
    }
  }

  return (
    <Box sx={{ width: '100%', marginTop: '30px', padding: '20px', minHeight: { md: '50vh' } }}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack spacing={2}>
          <TextField
            {...register('username')}
            label="Username"
            size='small'
            sx={{ bgcolor: theme.palette.primary.main, borderRadius: '5px' }}
            type="text"
            error={!!errors.username}
            helperText={errors.username?.message}
          />
          <TextField
            {...register('email', {
              required: 'Câmp obligatoriu',
              validate: value => parseEmail(value) === true || parseEmail(value)
            })}
            label="Email"
            size='small'
            sx={{ bgcolor: theme.palette.primary.main, borderRadius: '5px' }}
            type="email"
            error={!!errors.email}
            helperText={errors.email?.message}
          />
          <TextField
            {...register('company')}
            label="Firma"
            size='small'
            sx={{ bgcolor: theme.palette.primary.main, borderRadius: '5px' }}
            type="text"
          />
          <TextField
            label="CUI"
            size='small'
            value={userData.cui || ''}
            sx={{ bgcolor: theme.palette.primary.main, borderRadius: '5px' }}
            type="text"
            disabled
            helperText='CUI-ul ramane cel verificat la inregistrare.'
          />
          <TextField
            {...register('registration_no')}
            label="Nr. Reg. Com."
            size='small'
            sx={{ bgcolor: theme.palette.primary.main, borderRadius: '5px' }}
            type="text"
          />
          <TextField
            {...register('fiscal_address')}
            label="Adresa fiscala"
            size='small'
            multiline
            minRows={3}
            sx={{ bgcolor: theme.palette.primary.main, borderRadius: '5px' }}
            type="text"
          />
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <TextField
              {...register('county')}
              label="Judet"
              size='small'
              sx={{ bgcolor: theme.palette.primary.main, borderRadius: '5px', flex: 1 }}
              type="text"
            />
            <TextField
              {...register('city')}
              label="Oras"
              size='small'
              sx={{ bgcolor: theme.palette.primary.main, borderRadius: '5px', flex: 1 }}
              type="text"
            />
            <TextField
              {...register('postal_code')}
              label="Cod postal"
              size='small'
              sx={{ bgcolor: theme.palette.primary.main, borderRadius: '5px', flex: 1 }}
              type="text"
            />
          </Stack>
        <Controller
            name="phone"
            control={form.control} // Notice we're passing form.control here
            render={({ field }) => (
                <MuiTelInput
                {...field} // Spread the field object to pass value and onChange
                label="Telefon"
                size='small'
                defaultCountry='RO'
                onlyCountries={['RO']}
                sx={{ bgcolor: theme.palette.primary.main, borderRadius: '5px' }}
                InputLabelProps={{ style: { fontSize: '14px', color: theme.palette.fontColor.main } }}
                inputProps={{ style: { fontSize: '14px', color: theme.palette.fontColor.main } }}
                />
            )}
        />
          <Button
            type="submit"
            variant="outlined"
            size='small'
            color="accent"
            sx={{ borderRadius: '21px', bgcolor: theme.palette.primary.main }}
            disabled={loading || isFormUnchanged}
          >
            Salvează
          </Button>
        </Stack>
      </form>
      {/* Display update profile message */}
      {updateProfileMessage.message && (
        <Box color={updateProfileMessage.messageType === 'success' ? 'green' : 'red'}>
          {updateProfileMessage.message}
        </Box>
      )}
    </Box>
  )
}

ProfileForm.propTypes = {
  userData: PropTypes.shape({
    phone: PropTypes.string,
    email: PropTypes.string,
    cui: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    company: PropTypes.string,
    username: PropTypes.string,
    company_profile: PropTypes.shape({
      legal_name: PropTypes.string,
      registration_no: PropTypes.string,
      fiscal_address: PropTypes.string,
      county: PropTypes.string,
      city: PropTypes.string,
      postal_code: PropTypes.string
    })
  }).isRequired
}

export default ProfileForm
